import { Box, Button, Popover, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Column, ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";
// import { fetchPackages } from "slices/package/thunk";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { autocompleteDropdownStyles, filterPopoverStyles } from "Constants/Style";
import { CustomColumnDef } from "./UserManagementTableContainer";

interface FilterPopoverProps<TData> {
  column: Column<TData, unknown>;
  columnDef: CustomColumnDef<TData>;
  columnFilters?: ColumnFiltersState;
  setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
  setColValue?: any;
  iconColor?: string;

  //*// Backend format changed
  Rnet?: string;
}

const FilterPopover = ({
  column,
  columnDef,
  columnFilters,
  setColumnFilters,
  setColValue,
  iconColor,
  Rnet,
}: FilterPopoverProps<any>) => {
  const filterType = columnDef.filterType;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tempValue, setTempValue] = useState(column?.getFilterValue() || "");
  const [name, setName] = useState<string>("name");

  const [options, setOptions] = useState<any[]>([]);
  const [isLoading, setLoading] = useState(false);

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<Element>) => {
    setAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (!setColValue) return;

    if (tempValue && tempValue !== "" && tempValue !== "null") {
      column.setFilterValue(tempValue);
      setColValue((prev: any) => {
        const updated = (prev || []).filter(
          (f: { filterField: string }) => f.filterField !== column.id
        );
        return [...updated, { filterField: column.id, filterValue: tempValue }];
      });
    } else {
      column.setFilterValue(undefined);
      setColValue((prev: any) =>
        (prev || []).filter(
          (f: { filterField: string }) => f.filterField !== column.id
        )
      );
    }

    setAnchorEl(null);
  };

  const handleClear = () => {
    // Clear the filter from the table column
    column.setFilterValue(undefined);

    // Remove it from your local filter state
    if (setColValue) {
      setColValue((prev: any) =>
        (prev || []).filter(
          (f: { filterField: string }) => f.filterField !== column.id
        )
      );
    }

    // Clear the input value
    setTempValue("");
  };

  useEffect(() => {
    column.getFilterValue();
  }, []);

  useEffect(() => {
    if (!open) return;

    if (columnDef.filterOptions) {
      // Static filter options
      setOptions(columnDef.filterOptions);
      const appliedFilter = column.getFilterValue();
      setTempValue(appliedFilter || "");
    } else if (columnDef.filterFetchUrl) {
      // Dynamic filter options
      const fetchOptions = async () => {
        setLoading(true);
        try {
          const response = await Axios.get(columnDef.filterFetchUrl || "");
          const data: any = response?.data?.data?.[column?.id] || response?.data?.data || response?.data?.[column?.id];
          let result: any[] = [];

          if (Array.isArray(data)) {
            if (typeof data[0] === "object") {
              result = data.map(item => item?.[column?.id]);
            } else {
              // Already strings
              result = data;
            }
          } else {
            result = [];
          }
          setOptions(result);
        } catch (error) {
          toast.error("Failed to fetch filter options");
          console.error(error);
        } finally {
          setLoading(false);
        }
      };

      fetchOptions();

      const appliedFilter = column.getFilterValue();
      setTempValue(appliedFilter || "");
    }
  }, [open, columnDef.filterOptions, columnDef.filterFetchUrl]);

  return (
    <>
      <i
        className="ri-filter-line"
        onClick={handleOpen}
        style={{ cursor: "pointer", marginLeft: 6, color: iconColor, fontSize: "14px" }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box p={2} width={200} sx={filterPopoverStyles.container}>
          {filterType === "dropdown" ? (
            <Autocomplete
              sx={{ width: "100%", textTransform: "capitalize" }}
              size="small"
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              id="filter-autocomplete"
              options={options || []}
              loading={isLoading}
              value={
                options?.find((opt) => {
                  if (
                    typeof opt === "string" &&
                    typeof tempValue === "string"
                  ) {
                    return opt === tempValue;
                  }
                  return String(opt) === String(tempValue);
                }) ?? null
              }
              getOptionLabel={(option: any) => option?.toString()}
              isOptionEqualToValue={(option: any, value: any) => {
                if (typeof option === "string" && typeof value === "string") {
                  return option === value;
                }
                return String(option) === String(value);
              }}
              onChange={(_, newValue) => {
                if (newValue === null || newValue === undefined) {
                  setTempValue("");
                } else {
                  setTempValue(String(newValue));
                }
              }}
              renderOption={(props, option: any, state) => {
                const { key, ...rest } = props;
                return (
                  <Box key={state.index} component="li" {...rest}>
                    {option?.toString()}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    ...filterPopoverStyles.autocompleteTextField,
                  }}
                />
              )}
            />
          ) : (
            <TextField
              fullWidth
              label="Search"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              size="small"
              sx={filterPopoverStyles.textField}
            />
          )}

          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleClear}
              sx={filterPopoverStyles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApply}
              sx={filterPopoverStyles.applyButton}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default FilterPopover;
